import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { constNull, pipe } from 'fp-ts/function';
import { InjectedFormProps, reduxForm } from 'redux-form';
import { Stack, StructuredDate } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { Identifiable } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import {
    ApplicationState,
    WithAvailabilityBlockId,
    WithResourceCategoryId,
    WithServiceId,
    WithTimeUnit,
} from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { ResponsiveTwoColumns, SpacedForwardSlash } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    dateString,
    minMaxNumber,
    NumberInputField,
    presence,
    SelectField,
    TimeUnitIntervalFields,
    validation,
} from '@mews-commander/legacy-form';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import {
    getBulkAdjustAvailabilitiesInitialValues,
    getBulkAdjustAvailabilitiesMaxDate,
    getBulkAdjustAvailabilitiesMinDate,
    getBulkAdjustEndHourMaxValue,
    getBulkAdjustEndHourMinValue,
    getBulkAdjustStartHourMaxValue,
    getBulkAdjustStartHourMinValue,
    getBulkUpdateAllowedDateTimeUnitData,
    getResourceCategoryMaxPickedUpValue,
    getTotalReservationCapacityByResourceCategoryInBlock,
} from '../../../selectors';
import { END_DATE, END_HOUR, START_DATE, START_HOUR } from '../../../constants';
import { ResourceCategoryField } from '../../../types';
import { BulkAdjustAvailabilitiesFormValues } from './types';

const amountProps = {
    label: <LocalizedText textKey={TextKey.BlockedAmount} />,
    name: 'amount',
    required: true,
    min: 0,
};

const startProps = {
    required: true,
    label: <LocalizedText textKey={TextKey.StartOn} />,
};

const endProps = {
    required: true,
    label: <LocalizedText textKey={TextKey.EndOn} />,
};

const timeProps = {
    endProps: {
        label: <LocalizedText textKey={TextKey.AtTime} />,
        required: true,
    },
    startProps: {
        label: <LocalizedText textKey={TextKey.AtTime} />,
        required: true,
    },
};

const timeUnitSelectProps = {
    name: 'allowedDateTimeUnitIndexes',
    label: <LocalizedText textKey={TextKey.ApplyOn} />,
    required: true,
    multiselect: true,
};

const commonDateConstraints = {
    dateString,
    presence,
};

const dateConstraints = {
    startDate: commonDateConstraints,
    endDate: commonDateConstraints,
};
export interface StateProps extends WithTimeUnit {
    endHourMaxValue: string | null;
    endHourMinValue: string | null;
    startHourMaxValue: string | null;
    startHourMinValue: string | null;
    maxDate: StructuredDate | null;
    minDate: StructuredDate | null;
    initialValues: InjectedFormProps<BulkAdjustAvailabilitiesFormValues, ComposedProps>['initialValues'];
    totalReservationCapacity: number;
    maxBlockPickups: number;
}

interface OwnProps extends Identifiable, WithAvailabilityBlockId, WithResourceCategoryId, WithServiceId {
    form: string;
    field: ResourceCategoryField;
}

interface ComposedProps extends StateProps, OwnProps {}

export type BulkAdjustAvailabilitiesProps = ComposedProps &
    InjectedFormProps<BulkAdjustAvailabilitiesFormValues, ComposedProps>;

const BulkAdjustAvailabilitiesContent = ({
    endHourMaxValue,
    endHourMinValue,
    maxDate,
    minDate,
    startHourMaxValue,
    startHourMinValue,
    timeUnit,
    availabilityBlockId,
    resourceCategoryId,
}: BulkAdjustAvailabilitiesProps) => {
    const isHourlyService = timeUnit === TimeUnit.Hour;
    const optionSelector = getBulkUpdateAllowedDateTimeUnitData(timeUnit)?.optionSelector ?? constNull;
    const timeUnitOptions = useCommanderSelector(optionSelector);
    const totalReservationCapacity = useCommanderSelector((state) =>
        getTotalReservationCapacityByResourceCategoryInBlock(state, { availabilityBlockId, resourceCategoryId })
    );
    return (
        <Stack distribution="stretch" spacing={isHourlyService ? '200' : '100'} vertical>
            {isHourlyService ? (
                <ResponsiveTwoColumns>
                    <Stack vertical spacing="200">
                        <TimeUnitIntervalFields
                            fieldNames={[START_DATE, END_DATE]}
                            startProps={startProps}
                            endProps={endProps}
                            maxDate={maxDate}
                            minDate={minDate}
                            timeUnit={TimeUnit.Day}
                            constraints={dateConstraints}
                        />
                    </Stack>
                    <Stack vertical spacing="200">
                        <TimeUnitIntervalFields
                            startProps={{
                                ...timeProps.startProps,
                                maxValue: startHourMaxValue,
                                minValue: startHourMinValue,
                            }}
                            endProps={{
                                ...timeProps.endProps,
                                maxValue: endHourMaxValue,
                                minValue: endHourMinValue,
                            }}
                            fieldNames={[START_HOUR, END_HOUR]}
                            timeUnit={timeUnit}
                        />
                    </Stack>
                </ResponsiveTwoColumns>
            ) : (
                <Stack spacing="200">
                    <TimeUnitIntervalFields
                        maxDate={maxDate}
                        minDate={minDate}
                        fieldNames={[START_DATE, END_DATE]}
                        startProps={startProps}
                        endProps={endProps}
                        constraints={dateConstraints}
                        timeUnit={timeUnit}
                    />
                </Stack>
            )}
            {!!timeUnitOptions && <SelectField {...timeUnitSelectProps} options={timeUnitOptions} />}
            <NumberInputField
                suffix={
                    <>
                        {SpacedForwardSlash}
                        {totalReservationCapacity}
                    </>
                }
                {...amountProps}
            />
        </Stack>
    );
};

const getConstraints = ({ totalReservationCapacity, maxBlockPickups }: BulkAdjustAvailabilitiesProps) => ({
    amount: {
        presence,
        minMaxNumber: minMaxNumber(maxBlockPickups, totalReservationCapacity, {
            textKey: TextKey.AvailabilityBlockAdjustmentRangeValidationErrorMessage,
            substitutes: { pickupValue: maxBlockPickups, availableValue: totalReservationCapacity },
        }),
    },
});

export default pipe(
    BulkAdjustAvailabilitiesContent,
    reduxForm<BulkAdjustAvailabilitiesFormValues, ComposedProps>({
        enableReinitialize: true,
        keepDirtyOnReinitialize: true,
        validate: validation(getConstraints),
        pure: true,
    }),
    connect<StateProps, {}, OwnProps, ApplicationState>(
        createStructuredSelector({
            endHourMaxValue: getBulkAdjustEndHourMaxValue,
            endHourMinValue: getBulkAdjustEndHourMinValue,
            startHourMaxValue: getBulkAdjustStartHourMaxValue,
            startHourMinValue: getBulkAdjustStartHourMinValue,
            initialValues: getBulkAdjustAvailabilitiesInitialValues,
            maxDate: getBulkAdjustAvailabilitiesMaxDate,
            minDate: getBulkAdjustAvailabilitiesMinDate,
            timeUnit: getTimeUnitByServiceId,
            totalReservationCapacity: getTotalReservationCapacityByResourceCategoryInBlock,
            maxBlockPickups: getResourceCategoryMaxPickedUpValue,
        })
    )
);
